"use client";

import { FeedEvents, UserEvents, trackEvent } from "@/analytics";

import { Check } from "lucide-react";
import { Comedian } from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { Follow } from "@/components/checkout";
import Image from "next/image";
import Link from "next/link";
import { SpecialRow } from "@/data/specials";
import { WatchCTAButton } from "@/components/special";
import { formatPriceFromCents } from "@punchup/utils/formatPriceFromCents";
import { TaggedComedian } from "@/components/feed/types";
import { Dialog, DialogContent } from "@/shadcn/ui/dialog";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { useState } from "react";

interface VideoFeedHeaderDesktopProps {
  comedian: Comedian;
  special: SpecialRow;
  postAge: number;
  profileImg: string;
  userFollowsComedian: boolean;
  userSubscribedToComedian: boolean | null;
  subscriptionPrice: number | null | undefined;
  isLoggedOutState: boolean;
  redirectTo: string;
  renderTimestamp: (postAge: number) => string;
  taggedComedians?: TaggedComedian[];
}

export default function VideoFeedHeaderDesktop({
  comedian,
  special,
  postAge,
  profileImg,
  userFollowsComedian,
  userSubscribedToComedian,
  subscriptionPrice,
  isLoggedOutState,
  redirectTo,
  renderTimestamp,
  taggedComedians,
}: VideoFeedHeaderDesktopProps) {
  const comedianPageUrl = `/${comedian.slug}`;
  const [isComedianListOpen, setIsComedianListOpen] = useState(false);

  // Stacked mode: 36px when tagged comedians present, dynamic overlap to fill 64px.
  const STACKED_PHOTO_SIZE = 36;
  const STACKED_TOTAL_WIDTH = 64;
  const totalPhotos = 1 + (taggedComedians?.length ?? 0);
  const stackMarginLeft =
    totalPhotos > 1
      ? -(
          STACKED_PHOTO_SIZE -
          (STACKED_TOTAL_WIDTH - STACKED_PHOTO_SIZE) / (totalPhotos - 1)
        )
      : 0;

  const taggedLinkClass =
    "hover:underline hover:underline-offset-2 duration-200 ease-in-out decoration-[transparent] hover:decoration-black transition-[text-decoration]";

  return (
    <div className="flex flex-row items-center justify-between px-3 md:px-0">
      <div className="flex flex-row items-center w-full gap-2.5 md:gap-3">
        {taggedComedians && taggedComedians.length > 0 ? (
          <div className="flex flex-row flex-shrink-0 items-center">
            <Link
              href={comedianPageUrl}
              onClick={() =>
                trackEvent(FeedEvents.CLICKED_COMEDIAN_AVATAR_VIDEO_CARD)
              }
              style={{ zIndex: taggedComedians.length + 1 }}
              className="relative block hover:border-primary duration-200 rounded-full h-[36px] w-[36px] ease-in-out border-2 border-gray-800 decoration-[transparent] hover:decoration-black transition-[border-color,opacity] overflow-hidden flex-shrink-0"
            >
              <Image
                src={profileImg}
                width={36}
                height={36}
                alt={`Profile picture of ${comedian.display_name}`}
                className="object-cover w-full h-full bg-gray-300 aspect-square"
              />
            </Link>
            {taggedComedians.map((tc, i) => (
              <Link
                key={tc.id}
                href={`/${tc.slug}`}
                style={{
                  marginLeft: `${stackMarginLeft}px`,
                  zIndex: taggedComedians.length - i,
                }}
                className="relative block rounded-full h-[36px] w-[36px] border-2 border-gray-800 overflow-hidden flex-shrink-0"
              >
                {tc.about_img ? (
                  <Image
                    src={getCDNImageURL(tc.about_img)}
                    width={36}
                    height={36}
                    alt={`Profile picture of ${tc.display_name}`}
                    className="object-cover w-full h-full bg-gray-300 aspect-square"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-300" />
                )}
              </Link>
            ))}
          </div>
        ) : (
          /* Original single avatar — unchanged */
          <div className="w-[50px] flex flex-col justify-center">
            <Link
              href={comedianPageUrl}
              onClick={() =>
                trackEvent(FeedEvents.CLICKED_COMEDIAN_AVATAR_VIDEO_CARD)
              }
              className="hover:border-primary duration-200 rounded-full h-[50px] w-[50px] ease-in-out border-2 border-gray-800 decoration-[transparent] hover:decoration-black transition-[border-color,opacity] overflow-clip"
            >
              <Image
                src={profileImg}
                width={50}
                height={50}
                alt={`Profile picture of ${comedian.display_name}`}
                className="object-cover w-full h-full bg-gray-300 aspect-square"
              />
            </Link>
          </div>
        )}
        <div className="flex flex-col items-start justify-center flex-1 gap-0">
          <h2 className="font-medium text-[20px] line-clamp-2 leading-normal">
            <Link
              href={comedianPageUrl}
              onClick={() =>
                trackEvent(FeedEvents.CLICKED_COMEDIAN_NAME_VIDEO_CARD)
              }
              className={taggedLinkClass}
            >
              {comedian.display_name}
            </Link>
            {taggedComedians && taggedComedians.length === 1 && (
              <>
                {" and "}
                <Link
                  href={`/${taggedComedians[0].slug}`}
                  className={taggedLinkClass}
                >
                  {taggedComedians[0].display_name}
                </Link>
              </>
            )}
            {taggedComedians && taggedComedians.length === 2 && (
              <>
                {", "}
                <Link
                  href={`/${taggedComedians[0].slug}`}
                  className={taggedLinkClass}
                >
                  {taggedComedians[0].display_name}
                </Link>
                {", and "}
                <Link
                  href={`/${taggedComedians[1].slug}`}
                  className={taggedLinkClass}
                >
                  {taggedComedians[1].display_name}
                </Link>
              </>
            )}
            {taggedComedians && taggedComedians.length > 2 && (
              <>
                {" and "}
                <button
                  onClick={() => setIsComedianListOpen(true)}
                  className={`${taggedLinkClass} font-medium`}
                >
                  {taggedComedians.length} others
                </button>
              </>
            )}
          </h2>
          <p className="text-[#909090] text-[12px] leading-tight">
            {renderTimestamp(postAge)}
          </p>
        </div>

        {/* Any time a special is paid (paid livestreams are included). */}
        {special.requires_purchase ? (
          <Link
            href={`/${comedian.slug}/${special.slug}/purchase`}
            className="btn w-[unset] px-3 md:px-10 py-2.5 md:py-3 text-xs md:text-sm font-medium"
          >
            <WatchCTAButton special={special} />
          </Link>
        ) : special.requires_subscription && !userSubscribedToComedian ? (
          // Any time a special requires a subscription, but the user is not subscribed to the comedian.
          <Link
            href={`/${comedian.slug}/${special.slug}/checkout`}
            className="btn w-[unset] px-3 md:px-10 py-2.5 md:py-3 text-xs md:text-sm font-medium"
          >
            Subscribe for{" "}
            {subscriptionPrice
              ? formatPriceFromCents(subscriptionPrice)
              : "full access"}
          </Link>
        ) : taggedComedians &&
          taggedComedians.length > 0 ? null : !special.requires_purchase &&
          userFollowsComedian ? (
          // Any time a special is not paid (includes free livestreams), but the user follows the comedian.
          <button
            type="button"
            disabled
            aria-disabled="true"
            onClick={() => trackEvent(FeedEvents.CLICKED_SUBSCRIBED_VIDEO_CARD)}
            className="flex flex-row items-center gap-0.5 md:gap-1 text-[14px] md:text-[16px] w-[unset] text-primary text-xs md:text-sm font-medium bg-transparent px-1 py-2.5 md:py-3 self-start"
          >
            <Check width={12} height={12} strokeWidth={3} />
            <span>Subscribed</span>
          </button>
        ) : (
          // Any time a special is not paid (includes free livestreams), and the user does not follow the comedian.
          <Follow
            redirectTo={redirectTo}
            buttonText="Subscribe"
            followedLocation={FOLLOW_LOCATIONS.FEED_VIDEO_CARD}
            isLoggedOutState={isLoggedOutState}
            comedianToFollow={comedian}
            customCSSButtonClass="btn w-[unset] px-3 md:px-10 py-2.5 md:py-3 text-xs md:text-sm font-medium"
            customCSSContainerClass="block"
            special={special}
            additionalEventToTrack={{
              event: UserEvents.CLICKED_UNLOCK_SPECIAL,
              eventProperties: {
                comedianId: comedian.id,
                comedianName: comedian.display_name,
                location: FOLLOW_LOCATIONS.FEED_VIDEO_CARD,
              },
            }}
          />
        )}
      </div>

      {taggedComedians && taggedComedians.length > 2 && (
        <Dialog open={isComedianListOpen} onOpenChange={setIsComedianListOpen}>
          <DialogContent className="z-[50] font-regular md:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="overflow-hidden">
              <h1 className="text-lg font-semibold font-sans normal-case text-center pb-2">
                Comedians
              </h1>
              <div className="space-y-1.5">
                <div className="flex items-center py-2">
                  <Link
                    href={comedianPageUrl}
                    className="hover:underline underline-offset-2"
                    onClick={() => setIsComedianListOpen(false)}
                  >
                    {comedian.display_name}
                  </Link>
                </div>
                {taggedComedians.map((c) => (
                  <div key={c.id} className="flex items-center py-2">
                    <Link
                      href={`/${c.slug}`}
                      className="hover:underline underline-offset-2"
                      onClick={() => setIsComedianListOpen(false)}
                    >
                      {c.display_name}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
